import type { ReactElement } from 'react';
import type { PackageListStateFilter } from '@kirin/api-client/contracts/agent-command-events';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const OPTIONS: ReadonlyArray<{ value: PackageListStateFilter; label: string }> =
  [
    { value: 'all', label: 'All' },
    { value: 'blocked', label: 'Blocked' },
    { value: 'allowed', label: 'Allowed' },
  ];

interface PackageListStateFilterControlProps {
  value: PackageListStateFilter;
  onChange: (next: PackageListStateFilter) => void;
}

/**
 * KIR-4443 (AC 10): "All | Blocked | Allowed" segmented control in the
 * Packages table toolbar. Allowed is every package whose Enforcement switch
 * is green — i.e. everything not blocked. Fully controlled.
 */
export function PackageListStateFilterControl({
  value,
  onChange,
}: PackageListStateFilterControlProps): ReactElement {
  return (
    <div
      role="group"
      aria-label="Filter by enforcement"
      className="inline-flex items-center"
    >
      {OPTIONS.map((option, index) => {
        const selected = option.value === value;
        return (
          <Button
            key={option.value}
            type="button"
            size="sm"
            variant={selected ? 'default' : 'outline'}
            aria-pressed={selected}
            onClick={() => {
              if (!selected) onChange(option.value);
            }}
            className={cn(
              'h-8 rounded-none px-3 text-xs font-medium shadow-none',
              index === 0 && 'rounded-l-md',
              index === OPTIONS.length - 1 && 'rounded-r-md',
              index > 0 && '-ml-px',
              selected && 'relative z-10'
            )}
          >
            {option.label}
          </Button>
        );
      })}
    </div>
  );
}
